import * as z from 'zod';

import { HistoricalTransactionId, LoanId, LoanImportId } from '../BrandedIds';
import { add } from '../utils/math';

export const HistoricalTransactionType = z.enum(['payment', 'deposit', 'disbursement', 'draw']);
export type HistoricalTransactionType = z.infer<typeof HistoricalTransactionType>;

// Accounting buckets that must sum to `total` for each type (mirrors the CSV import row schemas).
// Draws and deposits carry no buckets, so their totals are unconstrained.
export const HISTORICAL_PAYMENT_BUCKET_KEYS = [
  'principal',
  'additionalPrincipal',
  'interest',
  'escrow',
  'suspense',
  'fee',
  'lateFee',
  'advance',
  'reserve',
  'holdback',
  'other',
] as const;

export const HISTORICAL_DISBURSEMENT_BUCKET_KEYS = [
  'escrow',
  'suspense',
  'reserve',
  'holdback',
  'lossDraft',
  'other',
] as const;

export type HistoricalTransactionBucketKey =
  | (typeof HISTORICAL_PAYMENT_BUCKET_KEYS)[number]
  | (typeof HISTORICAL_DISBURSEMENT_BUCKET_KEYS)[number];

export const getHistoricalTransactionBucketKeys = (
  type: HistoricalTransactionType,
): readonly HistoricalTransactionBucketKey[] => {
  switch (type) {
    case 'payment':
      return HISTORICAL_PAYMENT_BUCKET_KEYS;
    case 'disbursement':
      return HISTORICAL_DISBURSEMENT_BUCKET_KEYS;
    case 'draw':
    case 'deposit':
      return [];
  }
};

/**
 * Returns an error message when the provided buckets don't sum to `total`, mirroring the CSV
 * import rule: the check only applies once at least one bucket is non-zero. Returns undefined
 * when valid (including types with no buckets).
 */
export const getHistoricalTransactionBucketSumError = (
  type: HistoricalTransactionType,
  values: { total: number } & Partial<Record<HistoricalTransactionBucketKey, number | null | undefined>>,
): string | undefined => {
  const bucketKeys = getHistoricalTransactionBucketKeys(type);
  if (bucketKeys.length === 0) return undefined;

  const hasNonZeroBucket = bucketKeys.some((key) => values[key]);
  if (!hasNonZeroBucket) return undefined;

  const bucketSum = bucketKeys.reduce((sum, key) => add(sum, values[key] ?? 0), 0);
  const total = add(values.total);
  if (bucketSum !== total) {
    return `Total (${total}) must equal the sum of the accounting bucket columns (${bucketSum})`;
  }
  return undefined;
};

export interface HistoricalTransaction {
  id: HistoricalTransactionId;
  loanId: LoanId;
  loanImportId?: LoanImportId;
  type: HistoricalTransactionType;
  transactionId?: string;
  paymentDate: Date;
  dueDate?: Date;
  method?: string;
  description?: string;
  disbursementType?: string;
  disbursementRecipient?: string;
  total: number;
  upb?: number;
  principal?: number;
  additionalPrincipal?: number;
  interest?: number;
  escrow?: number;
  suspense?: number;
  fee?: number;
  lateFee?: number;
  advance?: number;
  reserve?: number;
  holdback?: number;
  lossDraft?: number;
  other?: number;
  createdAt: Date;
  updatedAt: Date;
}
